import { ref, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { fetchByDepartment } from '@/use/repositories/stuckWorkWarnings/fetchByDepartment';
import { warning as toastWarning } from '@/use/toast/warning';
import { useI18n } from 'vue-i18n';

const DEFAULT_POLLING_HOURS = 2;
const INITIAL_DELAY_MS = 3000;
const STORAGE_KEY_PREFIX = 'stuck_work_last_shown';

const getStorageKey = (userId, departmentSlug) => `${STORAGE_KEY_PREFIX}_${userId}_${departmentSlug}`;

const getLastShown = (userId, departmentSlug) => {
  try {
    const raw = localStorage.getItem(getStorageKey(userId, departmentSlug));
    if (!raw) return null;
    const { timestamp, pollingHours } = JSON.parse(raw);
    return { timestamp, pollingHours };
  } catch {
    return null;
  }
};

const setLastShown = (userId, departmentSlug, pollingHours) => {
  try {
    localStorage.setItem(
      getStorageKey(userId, departmentSlug),
      JSON.stringify({ timestamp: Date.now(), pollingHours })
    );
  } catch {
    // ignore storage errors
  }
};

const AUTH_ROUTES = new Set([
  'Login', 'LoginChoose', 'TwoFactorAuth', 'Recover', 'Reset',
  'ExpiredPassword', 'Logout', 'Sso',
]);

export function useStuckWorkWarning() {
  const store = useStore();
  const router = useRouter();
  const i18n = useI18n();

  const showStuckWorkModal = ref(false);
  const stuckWorkPayload = ref(null);
  let pollingTimer = null;
  let initialDelayTimer = null;
  let pollingHours = DEFAULT_POLLING_HOURS;

  const getDepartmentSlug = () => store.state.mainRole?.team?.name ?? null;

  const shouldShowForCurrentUser = () => {
    const slug = getDepartmentSlug();
    if (!slug) return false;
    const roleSlug = store.state.mainRole?.role?.slug;
    const userRoles = store.state.user?.roles ?? [];
    const isManager = roleSlug === 'manager';
    const isCam = slug === 'cam';
    const isGessoTechnician = userRoles.some(
      (r) => r === 'postazione-colatura-gesso' || r === 'postazione-finitura-gesso'
    );
    return isManager || isCam || isGessoTechnician;
  };

  const isInsideApp = () => {
    const routeName = router.currentRoute.value?.name;
    return routeName && !AUTH_ROUTES.has(routeName);
  };

  const scheduleNextPoll = () => {
    if (pollingTimer) return;
    const ms = pollingHours * 60 * 60 * 1000;
    pollingTimer = setTimeout(() => {
      pollingTimer = null;
      checkStuckWork();
    }, ms);
  };

  const checkStuckWork = async () => {
    const slug = getDepartmentSlug();
    if (!slug || !isInsideApp()) return;

    const data = await fetchByDepartment(slug);

    if (data) {
      pollingHours = data.polling_hours ?? DEFAULT_POLLING_HOURS;
      if (data.stuck_count > 0) {
        const userId = store.state.user?.id;
        const lastShown = userId ? getLastShown(userId, slug) : null;
        const hoursSinceLastShown = lastShown
          ? (Date.now() - lastShown.timestamp) / (60 * 60 * 1000)
          : Infinity;

        if (hoursSinceLastShown >= (lastShown?.pollingHours ?? pollingHours)) {
          stuckWorkPayload.value = data;
          showStuckWorkModal.value = true;
          toastWarning(
            i18n.t('Stuck work toast', {
              count: data.stuck_count,
              department: data.department_name,
              hours: data.threshold_hours ?? 3,
            })
          );
          if (userId) setLastShown(userId, slug, pollingHours);
        }
      }
    } else {
      pollingHours = pollingHours ?? DEFAULT_POLLING_HOURS;
    }

    scheduleNextPoll();
  };

  const closeStuckWorkModal = () => {
    showStuckWorkModal.value = false;
    stuckWorkPayload.value = null;
  };

  const startPolling = () => {
    stopPolling();
    initialDelayTimer = setTimeout(() => {
      initialDelayTimer = null;
      checkStuckWork();
    }, INITIAL_DELAY_MS);
  };

  const stopPolling = () => {
    if (initialDelayTimer) {
      clearTimeout(initialDelayTimer);
      initialDelayTimer = null;
    }
    if (pollingTimer) {
      clearTimeout(pollingTimer);
      pollingTimer = null;
    }
  };

  // Start polling only when user has entered the app and is allowed to see the warning
  watch(
    () => router.currentRoute.value?.name,
    (routeName) => {
      if (
        routeName &&
        !AUTH_ROUTES.has(routeName) &&
        getDepartmentSlug() &&
        shouldShowForCurrentUser()
      ) {
        startPolling();
      } else {
        stopPolling();
      }
    },
    { immediate: true }
  );

  onUnmounted(stopPolling);

  return {
    showStuckWorkModal,
    stuckWorkPayload,
    closeStuckWorkModal,
  };
}
